// The Health instrument (GDD §8 + §15.1-1/-2/-4, M6): bar + erosion/yr, the
// frailty badge, the health warning ladder with years-ahead countdowns, and
// the itemised aging/overwork/misery/flourishing readout (unlocked by the
// Check-up/Doctor's Letter cards in M7; behind the ?checkup dev flag now).

import type { Params } from '@lifespan/engine';
import { eta, type Clock } from '../game/clock.ts';
import { healthModel, type Session } from '../game/driver.ts';

export function HealthMeter({
  session,
  P,
  clock,
  showItemised,
}: {
  session: Session;
  P: Params;
  clock: Clock;
  /** the Check-up readout (M7 card unlock; dev flag pre-M7) */
  showItemised: boolean;
}) {
  const h = healthModel(session, P);
  const barPct = Math.max(0, Math.min(100, h.H));
  const r = h.rates;

  return (
    <section className="panel health" aria-label="Health">
      <div className="panel-title">Health</div>
      <div className="health-row">
        <div
          className="health-bar"
          role="img"
          aria-label={`Health ${h.H.toFixed(0)} of 100`}
        >
          <div
            className={`health-fill ${h.frail ? 'frail' : ''}`}
            style={{ width: `${barPct}%` }}
          />
        </div>
        <span className="health-value">{h.H.toFixed(0)}</span>
        {h.frail && <span className="badge broke">FAILING · income ×0.60</span>}
      </div>

      <div className="health-lines">
        <div className="line">
          <span>Erosion at this plan</span>
          <span className={r.net < 0 ? '' : 'ok'}>
            {r.net >= 0 ? '+' : '−'}
            {Math.abs(r.net).toFixed(2)}/yr
          </span>
        </div>
        {showItemised && (
          <>
            <div className="line dim">
              <span>· aging (not a choice)</span>
              <span>−{r.aging.toFixed(2)}/yr</span>
            </div>
            {r.overwork > 0 && (
              <div className="line dim">
                <span>· overwork</span>
                <span>−{r.overwork.toFixed(2)}/yr</span>
              </div>
            )}
            {r.misery > 0 && (
              <div className="line dim">
                <span>· misery (burnout band)</span>
                <span>−{r.misery.toFixed(2)}/yr</span>
              </div>
            )}
            {r.flourishing > 0 && (
              <div className="line dim">
                <span>· flourishing</span>
                <span>+{r.flourishing.toFixed(2)}/yr</span>
              </div>
            )}
            {r.sabbatical > 0 && (
              <div className="line dim">
                <span>· sabbatical recovery</span>
                <span>+{r.sabbatical.toFixed(2)}/yr</span>
              </div>
            )}
          </>
        )}
      </div>

      <ul className="ladder" aria-label="Health warnings">
        {h.ladder.map((rung) => (
          <li key={rung.threshold} className={`rung ${rung.status}`}>
            {rung.status === 'below' && (
              <>
                <span className="rung-dot">●</span> {rung.label} (below {rung.threshold})
              </>
            )}
            {rung.status === 'approaching' && (
              <>
                <span className="rung-dot">◔</span> {rung.label} at {rung.threshold} — in{' '}
                {eta(rung.yearsToArrival!, clock)} at this plan
              </>
            )}
            {rung.status === 'clear' && (
              <>
                <span className="rung-dot">○</span> {rung.label} ({rung.threshold}) — not on
                this path
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
